import {
  Building05,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Trash02,
  Typography,
} from '@v2/ui';
import { cn } from '@v2/utils';
import { PaxCounter } from '@v2/views/buy/components/RoomConfigurator';
import { FormikProps } from 'formik';
import { useState } from 'react';
import { useMediaQuery } from 'usehooks-ts';
import { useCheckInOut } from '../../../hooks/use-hotel-info';
import { HeaderFormikProps } from '../Header';
import { InputWithLabel } from './InputWithLabel';

type Props = {
  formik: FormikProps<HeaderFormikProps>;
  triggerClassName?: string;
};

export function BaseRoomsFilter(props: Props) {
  const { formik, triggerClassName } = props;
  const [isOpen, setIsOpen] = useState(false);

  const { setFieldValue, values } = formik;
  const matches = useMediaQuery('(min-width: 1024px)');

  const [{ formattedValue }] = useCheckInOut({
    roomConfigurations: values.roomConfigurations,
  });

  const handleAddRoom = () => {
    const { roomConfigurations } = values;
    setFieldValue('roomConfigurations', [
      ...roomConfigurations,
      {
        adults: 1,
        children: [],
        infants: 0,
      },
    ]);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <InputWithLabel
          triggerClassName={triggerClassName}
          icon={
            <Building05
              className="text-gray-800"
              pathProps={{ stroke: 'currentColor' }}
            />
          }
          label={'Rooms'}
          value={formattedValue}
        />
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        sideOffset={4}
        collisionPadding={16}
        className="popover-content-width-same-as-its-trigger z-[60] flex max-h-[min(540px,calc(100dvh-100px))] max-w-[calc(100vw-32px)] flex-col bg-white p-4 shadow-xl md:w-auto"
        align="start"
      >
        {!matches && (
          <div className="mb-3 flex shrink-0 items-center justify-between">
            <Typography size="tmd" className="font-semibold">
              Rooms
            </Typography>
            <Button
              type="button"
              variant="link"
              className="min-h-[36px] p-0 font-semibold"
              disabled={values.roomConfigurations.length >= 8}
              onClick={handleAddRoom}
            >
              + Add room
            </Button>
          </div>
        )}
        <div
          data-vaul-no-drag
          style={{ WebkitOverflowScrolling: 'touch' }}
          className="flex max-h-[340px] min-h-0 flex-1 touch-pan-y flex-col gap-4 overflow-y-auto overscroll-y-contain pr-1 sm:max-h-[400px] md:max-h-[60dvh] md:w-[552px] md:flex-row md:flex-wrap"
        >
          {values.roomConfigurations?.map((configuration, roomIndex) => (
            <div
              key={roomIndex}
              data-vaul-no-drag
              className="min-h-[228px] w-full touch-pan-y space-y-1.5 rounded-lg border border-gray-300 p-4 md:min-h-[240px] md:w-[240px]"
            >
              <div className="flex items-center justify-between">
                <Typography
                  variant="h3"
                  size="tsm"
                  className="font-medium text-gray-500"
                >{`Room ${roomIndex + 1}`}</Typography>
                <button
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-md transition-colors',
                    values.roomConfigurations.length === 1
                      ? 'cursor-not-allowed text-gray-400'
                      : 'cursor-pointer text-gray-700 hover:bg-gray-100'
                  )}
                  type="button"
                  aria-label={`Remove room ${roomIndex + 1}`}
                  tabIndex={0}
                  disabled={values.roomConfigurations.length === 1}
                  onClick={() => {
                    const rooms = [...values.roomConfigurations];
                    rooms.splice(roomIndex, 1);
                    setFieldValue('roomConfigurations', rooms);
                  }}
                >
                  <Trash02 size="20" className="shrink-0" />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <Typography
                  size="tmd"
                  variant="h4"
                  className="flex flex-col font-medium"
                >
                  Adults
                  <Typography size="txs" className="mb-1 text-gray-500">
                    Age over 17
                  </Typography>
                </Typography>
                {/* eslint-disable-next-line max-len */}
                <PaxCounter
                  value={configuration.adults}
                  onIcrement={() => {
                    setFieldValue(
                      `roomConfigurations.${roomIndex}.adults`,
                      configuration.adults + 1
                    );
                  }}
                  onDecrement={() => {
                    if (configuration.adults > 1) {
                      setFieldValue(
                        `roomConfigurations.${roomIndex}.adults`,
                        configuration.adults - 1
                      );
                    }
                  }}
                />
              </div>
              <div className="flex items-center justify-between">
                <Typography
                  size="tmd"
                  variant="h4"
                  className="flex flex-col font-medium"
                >
                  Children
                  <Typography size="txs" className="mb-1 text-gray-500">
                    Age 2-17
                  </Typography>
                </Typography>
                {/* eslint-disable-next-line max-len */}
                <PaxCounter
                  value={configuration.children.length}
                  onIcrement={() => {
                    const { children } =
                      values?.roomConfigurations?.[roomIndex];
                    setFieldValue(`roomConfigurations.${roomIndex}.children`, [
                      ...children,
                      0,
                    ]);
                  }}
                  onDecrement={() => {
                    const children = [
                      ...values?.roomConfigurations?.[roomIndex].children,
                    ];
                    children.pop();
                    setFieldValue(
                      `roomConfigurations.${roomIndex}.children`,
                      children
                    );
                  }}
                />
              </div>

              {configuration.children.map((age, childIndex) => (
                <label key={childIndex} className="block text-sm">
                  Child {childIndex + 1} age
                  <select
                    value={age || ''}
                    className="mt-1 h-11 w-full rounded-lg border bg-white px-3 text-base"
                    onChange={(event) =>
                      setFieldValue(
                        `roomConfigurations.${roomIndex}.children.${childIndex}`,
                        Number(event.target.value)
                      )
                    }
                  >
                    <option value="" disabled>
                      Choose age
                    </option>
                    {Array.from({ length: 16 }, (_, index) => index + 2).map(
                      (value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      )
                    )}
                  </select>
                </label>
              ))}

              <div className="flex items-center justify-between">
                <Typography
                  size="tmd"
                  variant="h4"
                  className="flex flex-col font-medium"
                >
                  Infants
                  <Typography size="txs" className="mb-1 text-gray-500">
                    Age under 2
                  </Typography>
                </Typography>
                {/* eslint-disable-next-line max-len */}
                <PaxCounter
                  value={configuration.infants}
                  onIcrement={() => {
                    setFieldValue(
                      `roomConfigurations.${roomIndex}.infants`,
                      configuration.infants + 1
                    );
                  }}
                  onDecrement={() => {
                    if (configuration.infants - 1 !== -1)
                      setFieldValue(
                        `roomConfigurations.${roomIndex}.infants`,
                        configuration.infants - 1
                      );
                  }}
                />
              </div>
            </div>
          ))}

          {matches && (
            <div className="hidden h-[240px] w-[240px] items-center justify-center rounded-lg border border-dashed md:flex">
              <Button
                type="button"
                variant="link"
                className="p-0"
                onClick={handleAddRoom}
                disabled={values.roomConfigurations.length >= 8}
              >
                + Add new room
              </Button>
            </div>
          )}
        </div>
        {!matches && (
          <div className="mt-3 flex shrink-0 justify-end border-t pt-3">
            <Button
              type="button"
              className="w-full"
              onClick={() => setIsOpen(false)}
            >
              Done
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
